/**
 * The SFX Lab renderer, ported.
 *
 * This is the deterministic offline synthesizer from SFX Lab
 * (https://github.com/andywise/sfx-lab, `src/audio-engine.js`), stripped to the
 * render path: recipe JSON in, stereo Float32Array pair out. The recipes under
 * `src/sfx/` are plain SFX Lab documents, and keeping this code line-for-line
 * with the lab's is what makes the round trip honest — a recipe auditioned in
 * the lab and the same recipe played in-game come out of the same math, sample
 * for sample.
 *
 * So: fix rendering bugs upstream in SFX Lab first, then mirror them here. The
 * one deliberate difference is that `renderRecipe` is synchronous — the game
 * renders each recipe once into an AudioBuffer and plays it from cache, so
 * there is nothing to await.
 */

const TAU = Math.PI * 2;

export function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function seededRandom(seed = 1) {
  let s = (Number(seed) || 1) >>> 0;
  return () => {
    s += 0x6D2B79F5;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function panGains(pan) {
  const angle = (Math.max(-1, Math.min(1, pan)) + 1) * Math.PI / 4;
  return [Math.cos(angle), Math.sin(angle)];
}

function addSample(buffer, index, left, right = left) {
  if (index < 0 || index >= buffer[0].length) return;
  buffer[0][index] += left;
  buffer[1][index] += right;
}

function envelope(t, attack, decay, curve = 3) {
  if (t < 0) return 0;
  if (attack > 0 && t < attack) return Math.pow(t / attack, 1.5);
  const dt = Math.max(0, t - attack);
  return Math.exp(-curve * dt / Math.max(0.001, decay));
}

function filteredNoiseState() {
  return { lp: 0, hp: 0, prevLp: 0 };
}

function noiseColor(raw, color, state) {
  state.lp += 0.08 * (raw - state.lp);
  const pinkish = raw * 0.35 + state.lp * 0.65;
  if (color === 'pink') return pinkish;
  if (color === 'brown') {
    state.hp = (state.hp + raw * 0.02) / 1.02;
    return state.hp * 3.2;
  }
  return raw;
}

function onePoleBand(sample, state, lowpass, highpass, sampleRate) {
  const lpAlpha = 1 - Math.exp(-TAU * Math.min(lowpass, sampleRate * 0.45) / sampleRate);
  state.lp += lpAlpha * (sample - state.lp);
  const hpAlpha = 1 - Math.exp(-TAU * Math.max(20, highpass) / sampleRate);
  state.prevLp += hpAlpha * (state.lp - state.prevLp);
  return state.lp - state.prevLp;
}

function transientSignal(type, phase, noise, brightness) {
  const b = Math.max(0, Math.min(1, brightness));
  switch (type) {
    case 'glass': return Math.sin(phase) * 0.72 + Math.sin(phase * 2.73) * 0.2 + noise * 0.08 * b;
    case 'gold': return Math.sin(phase) * 0.55 + Math.sin(phase * 1.51) * 0.25 + Math.sin(phase * 3.97) * 0.12 + noise * 0.06;
    case 'stone': return Math.sin(phase * 0.36) * 0.52 + noise * 0.48 * (1 - b * 0.4);
    case 'wood': return Math.sin(phase * 0.68) * 0.42 + noise * 0.58 * (1 - b * 0.55);
    case 'mechanical': return Math.sign(Math.sin(phase)) * 0.28 + Math.sin(phase * 0.48) * 0.25 + noise * 0.47;
    case 'chip':
    default: return Math.sin(phase) * 0.5 + Math.sin(phase * 1.92) * 0.22 + noise * 0.28;
  }
}

export function renderRecipe(recipe, sampleRate = 44100) {
  const duration = Math.max(0.15, Number(recipe.duration) || 1);
  const tail = Math.max(0.2, (recipe.space?.reverbTime || 0) * (recipe.space?.reverb || 0));
  const totalDuration = Math.min(12, duration + tail);
  const length = Math.ceil(totalDuration * sampleRate);
  const dry = [new Float32Array(length), new Float32Array(length)];
  const rand = seededRandom(recipe.seed || 1);
  const layers = recipe.layers || {};
  const width = recipe.space?.stereoWidth ?? 0.4;

  if (layers.transient?.enabled) renderTransient(dry, layers.transient, sampleRate, rand, width);
  if (layers.tone?.enabled) renderTone(dry, layers.tone, sampleRate, rand, width);
  if (layers.sub?.enabled) renderSub(dry, layers.sub, sampleRate, rand);
  if (layers.sparkle?.enabled) renderSparkle(dry, layers.sparkle, sampleRate, rand, width);
  if (layers.noise?.enabled) renderNoise(dry, layers.noise, sampleRate, rand, width);

  applyDelay(dry, recipe.space || {}, sampleRate);
  applyReverb(dry, recipe.space || {}, sampleRate, rand);
  applyMaster(dry, recipe.masterGain ?? 0.8, recipe.dynamics || {});

  return { channels: dry, sampleRate, duration: totalDuration, length };
}

function renderTransient(out, p, sr, rand, width) {
  const start = Math.floor((p.time || 0) * sr);
  const length = Math.floor(Math.max(0.01, p.decay || 0.05) * 5 * sr);
  const freq = Math.max(80, p.pitch || 1400);
  const [gl, gr] = panGains((rand() * 2 - 1) * width * 0.15);
  let phase = 0;
  for (let i = 0; i < length; i++) {
    const t = i / sr;
    const env = Math.exp(-6 * t / Math.max(0.008, p.decay || 0.05));
    const sweep = 1 + 0.18 * Math.exp(-18 * t);
    phase += TAU * freq * sweep / sr;
    const raw = rand() * 2 - 1;
    const sig = transientSignal(p.type, phase, raw, p.brightness ?? 0.5) * env * (p.gain ?? 0.4);
    addSample(out, start + i, sig * gl, sig * gr);
  }
}

function waveformSample(type, phase) {
  if (type === 'triangle') return 2 / Math.PI * Math.asin(Math.sin(phase));
  if (type === 'square') return Math.sign(Math.sin(phase));
  if (type === 'sawtooth') return 2 * ((phase / TAU) - Math.floor(phase / TAU + 0.5));
  return Math.sin(phase);
}

function renderTone(out, p, sr, rand, width) {
  const notes = Array.isArray(p.notes) ? p.notes : [72];
  notes.forEach((note, index) => {
    const startTime = (p.time || 0) + index * (p.stagger || 0);
    const start = Math.floor(startTime * sr);
    const attack = Math.max(0.001, p.attack || 0.003);
    const decay = Math.max(0.02, p.decay || 0.3);
    const length = Math.floor((attack + decay * 5) * sr);
    const detune = ((p.detune || 0) * (rand() * 2 - 1)) / 1200;
    const freq = midiToFreq(Number(note)) * Math.pow(2, detune);
    const pan = notes.length === 1 ? 0 : ((index / Math.max(1, notes.length - 1)) * 2 - 1) * width;
    const [gl, gr] = panGains(pan);
    let phase = 0;
    for (let i = 0; i < length; i++) {
      const t = i / sr;
      phase += TAU * freq / sr;
      const env = envelope(t, attack, decay, 4.2);
      const body = waveformSample(p.waveform, phase);
      const harmonic = Math.sin(phase * 2.01) * 0.09 + Math.sin(phase * 3.98) * 0.035;
      const sig = (body * 0.86 + harmonic) * env * (p.gain ?? 0.5) / Math.sqrt(notes.length);
      addSample(out, start + i, sig * gl, sig * gr);
    }
  });
}

function renderSub(out, p, sr) {
  const start = Math.floor((p.time || 0) * sr);
  const decay = Math.max(0.03, p.decay || 0.3);
  const length = Math.floor(decay * 5 * sr);
  let phase = 0;
  for (let i = 0; i < length; i++) {
    const t = i / sr;
    const freq = Math.max(28, (p.frequency || 70) * (1 + (p.drop || 0.2) * Math.exp(-8 * t)));
    phase += TAU * freq / sr;
    const env = Math.exp(-4.8 * t / decay);
    const sig = Math.sin(phase) * env * (p.gain ?? 0.2);
    addSample(out, start + i, sig, sig);
  }
}

function renderSparkle(out, p, sr, rand, width) {
  const count = Math.max(0, Math.round(p.count || 0));
  for (let n = 0; n < count; n++) {
    const time = (p.time || 0) + rand() * (p.spread || 0.3);
    const start = Math.floor(time * sr);
    const decay = Math.max(0.015, (p.decay || 0.12) * (0.7 + rand() * 0.6));
    const length = Math.floor(decay * 5 * sr);
    const min = p.minFrequency || 4000;
    const max = Math.max(min + 10, p.maxFrequency || 9000);
    const freq = min * Math.pow(max / min, rand());
    const pan = (rand() * 2 - 1) * width;
    const [gl, gr] = panGains(pan);
    let phase = rand() * TAU;
    for (let i = 0; i < length; i++) {
      const t = i / sr;
      phase += TAU * freq / sr;
      const env = Math.exp(-6 * t / decay);
      const sig = (Math.sin(phase) * 0.75 + Math.sin(phase * 1.47) * 0.25) * env * (p.gain ?? 0.15) / Math.sqrt(Math.max(1, count * 0.6));
      addSample(out, start + i, sig * gl, sig * gr);
    }
  }
}

function renderNoise(out, p, sr, rand, width) {
  const start = Math.floor((p.time || 0) * sr);
  const attack = Math.max(0.001, p.attack || 0.001);
  const decay = Math.max(0.015, p.decay || 0.15);
  const length = Math.floor((attack + decay * 5) * sr);
  const pan = (rand() * 2 - 1) * width * 0.5;
  const [gl, gr] = panGains(pan);
  const colorState = filteredNoiseState();
  const bandState = filteredNoiseState();
  for (let i = 0; i < length; i++) {
    const t = i / sr;
    const raw = noiseColor(rand() * 2 - 1, p.color, colorState);
    const band = onePoleBand(raw, bandState, p.lowpass || 12000, p.highpass || 2000, sr);
    const env = envelope(t, attack, decay, 4.4);
    const sig = band * env * (p.gain ?? 0.05);
    addSample(out, start + i, sig * gl, sig * gr);
  }
}

function applyDelay(channels, p, sr) {
  const mix = Math.max(0, Math.min(0.8, p.delay || 0));
  if (!mix) return;
  const delaySamples = Math.max(1, Math.floor((p.delayTime || 0.14) * sr));
  const fb = Math.max(0, Math.min(0.75, p.feedback || 0.15));
  for (let c = 0; c < 2; c++) {
    const data = channels[c];
    for (let i = delaySamples; i < data.length; i++) {
      data[i] += data[i - delaySamples] * mix;
      if (i + delaySamples < data.length) data[i + delaySamples] += data[i] * fb * mix * 0.5;
    }
  }
}

function applyReverb(channels, p, sr, rand) {
  const mix = Math.max(0, Math.min(0.9, p.reverb || 0));
  if (!mix) return;
  const time = Math.max(0.1, p.reverbTime || 1);
  const pre = Math.floor((p.preDelay || 0) * sr);
  const taps = Math.max(8, Math.min(72, Math.floor(time * 24)));
  const source = [channels[0].slice(), channels[1].slice()];
  for (let t = 0; t < taps; t++) {
    const normalized = (t + 1) / taps;
    const tapTime = pre + Math.floor((0.018 + normalized * time * (0.22 + rand() * 0.78)) * sr);
    const decay = Math.pow(1 - normalized, 1.9) * mix * (0.12 + rand() * 0.08);
    const cross = rand() > 0.5;
    for (let c = 0; c < 2; c++) {
      const from = source[cross ? 1 - c : c];
      const to = channels[c];
      for (let i = 0; i + tapTime < to.length; i += 1) {
        to[i + tapTime] += from[i] * decay;
      }
    }
  }
}

function applyMaster(channels, gain, dynamics) {
  const drive = 1 + Math.max(0, dynamics.compressor || 0) * 2.4;
  const limit = Math.max(0.1, Math.min(0.999, dynamics.limiter || 0.94));
  let peak = 0;
  for (const data of channels) for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]));
  const normalize = peak > limit ? limit / peak : 1;
  for (const data of channels) {
    for (let i = 0; i < data.length; i++) {
      const x = data[i] * gain * normalize;
      data[i] = Math.tanh(x * drive) / Math.tanh(drive) * limit;
    }
  }
}
